import { z } from "zod";
import { folderIdSchema, optionalFolderIdSchema } from "./folders.ts";
import {
  documentIdSchema,
  readerSchema,
  storedDocumentOriginSchema,
  storedReaderSchema,
  storedTrustSchema,
} from "./knowledge.ts";
import { projectIdSchema } from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";

/**
 * Project wiki and declassification (openspec change `project-wiki`). A published page is a
 * project document of kind `wiki_page`: its readers, classification, origin, passages and
 * citations are a document's, checked by `KnowledgeAccess`. Drafts are proposals in the review
 * queue; only people publish them. Declassification is the only path that widens a label
 * (ADR 0009 amendment).
 */

export const wikiPageTypeSchema = z.enum(["summary", "concept", "entity", "other"]);
export type WikiPageType = z.infer<typeof wikiPageTypeSchema>;

/** Longest Markdown a draft or page holds (characters); model output is held to it too. */
export const WIKI_MARKDOWN_MAX = 200_000;
/** Longest page title (characters): the API schema and the OKF check share it. */
export const WIKI_TITLE_MAX = 200;
export const wikiTitleSchema = z.string().trim().min(1).max(WIKI_TITLE_MAX);
export const wikiMarkdownSchema = z.string().max(WIKI_MARKDOWN_MAX);

/** The slug `[[Title]]` links resolve to: lower case, whitespace collapsed. */
export function wikiSlug(title: string): string {
  return title.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
}

/** `[[Title]]` and `[[Title|label]]` targets in Markdown, in order, once each (by slug). */
export function wikiLinkTargets(markdown: string): string[] {
  const seen = new Map<string, string>();
  for (const match of markdown.matchAll(/\[\[([^[\]|\n]{1,200})(?:\|[^[\]\n]{0,200})?\]\]/g)) {
    const title = (match[1] ?? "").trim();
    if (title && !seen.has(wikiSlug(title))) {
      seen.set(wikiSlug(title), title);
    }
  }
  return [...seen.values()];
}

/**
 * Why a readable page needs checking (project-home design D9), the first that holds: a source
 * changed that the viewer may see, its review is due, or it was verified once and has a newer
 * version since. A reading aid only: it blocks nothing.
 */
export const wikiCheckReasonSchema = z.enum(["source_changed", "review_due", "unverified"]);
export type WikiCheckReason = z.infer<typeof wikiCheckReasonSchema>;

/** A published page as listed (index) for a viewer who can read it. */
export const wikiPageSummarySchema = z.object({
  id: documentIdSchema,
  title: z.string(),
  pageType: wikiPageTypeSchema,
  version: z.int().min(1),
  /** `processing` while the current version is being indexed. */
  status: z.enum(["uploading", "processing", "ready", "failed"]),
  /** The current version contains declassified content (shown to the owner and managers). */
  declassified: z.boolean(),
  updatedAt: z.string(),
  /** Its folder (knowledge-folders); null or absent: the root of its section. */
  folderId: folderIdSchema.nullable().optional(),
  /** Why it needs checking, for this viewer (wiki index only); absent when it does not. */
  check: wikiCheckReasonSchema.optional(),
});
export type WikiPageSummary = z.infer<typeof wikiPageSummarySchema>;

export const wikiVersionInfoSchema = z.object({
  changeSetId: z.uuid().nullable().optional(),
  changeSetTitle: z.string().nullable().optional(),
  origin: z.enum(["person", "agent", "answer", "mcp", "ingestion", "revert"]).nullable().optional(),
  publishedByName: z.string().nullable().optional(),
  publishedBy: userIdSchema.nullable().optional(),
  op: z.enum(["create", "edit", "rename", "move", "delete"]).nullable().optional(),
  version: z.int().min(1),
  publishedAt: z.string(),
  authorId: userIdSchema,
  authorName: z.string().nullable(),
  declassified: z.boolean(),
});
export type WikiVersionInfo = z.infer<typeof wikiVersionInfoSchema>;

/** One version of a page as it was published: its own title, type, folder and Markdown. */
export const wikiVersionSnapshotSchema = z.object({
  version: z.int().min(1),
  title: z.string(),
  pageType: wikiPageTypeSchema,
  folderId: folderIdSchema.nullable(),
  markdown: z.string(),
  authorName: z.string().nullable(),
  publishedAt: z.string(),
  /** The change set that published it; null when the viewer may not see it whole. */
  changeSetId: z.uuid().nullable().optional(),
  changeSetTitle: z.string().nullable().optional(),
});
export type WikiVersionSnapshot = z.infer<typeof wikiVersionSnapshotSchema>;

/** Two versions of a page the viewer may open, oldest first (the client computes the diff). */
export const wikiVersionCompareSchema = z.object({
  from: wikiVersionSnapshotSchema,
  to: wikiVersionSnapshotSchema,
});
export type WikiVersionCompare = z.infer<typeof wikiVersionCompareSchema>;

export const wikiPageRefSchema = z.object({ id: documentIdSchema, title: z.string() });

/**
 * How the shown version was written (ADR 0015): its change set's origin and actor kind, and the
 * agent's model role (never a model ID). The conversation and the MCP client are named only to a
 * viewer who may see that change set whole.
 */
export const wikiGeneratedSchema = z.object({
  origin: z.enum(["person", "agent", "answer", "mcp", "ingestion", "revert"]),
  // `wikiActorKindSchema` (wiki-change-sets.ts imports this module).
  actorKind: z.enum(["user", "agent", "mcp_client", "job"]),
  modelRole: z.string().nullable().optional(),
  conversationId: z.uuid().nullable().optional(),
  clientName: z.string().nullable().optional(),
});
export type WikiGenerated = z.infer<typeof wikiGeneratedSchema>;

/** A person named on a page (owner, verifier): by ID, with their display name. */
export const wikiPersonSchema = z.object({ id: userIdSchema, name: z.string().nullable() });

/** `PUT …/pages/:page/owner`: a project member who reads the page, or null to clear it. */
export const setWikiPageOwnerRequestSchema = z.object({ userId: z.uuid().nullable() });
/** `POST …/pages/:page/verification`: the version being attested (the current one). */
export const verifyWikiPageRequestSchema = z.object({ version: z.int().min(1) });

/** One version of a page, as a reader opens it (a citation of an older one too). */
export const wikiPageViewSchema = wikiPageSummarySchema.extend({
  /** The version shown, and its Markdown (rendered without raw HTML by clients). */
  shownVersion: z.int().min(1),
  markdown: z.string(),
  /** A newer version than the one shown exists. */
  newerVersion: z.boolean(),
  versions: z.array(wikiVersionInfoSchema),
  /** Pages the viewer can read that link here. */
  backlinks: z.array(wikiPageRefSchema),
  /** `[[links]]` of the shown version: the page when the viewer can read it, else null. */
  links: z.array(z.object({ title: z.string(), page: wikiPageRefSchema.nullable() })),
  readers: z.array(storedReaderSchema),
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  /**
   * Project admins only: the shown version's derivation sources (declassified ones included),
   * titled only when the admin can read them.
   */
  provenance: z
    .array(
      z.object({
        id: documentIdSchema.nullable(),
        title: z.string().nullable(),
        declassified: z.boolean(),
      }),
    )
    .nullable(),
  /** How the shown version was written; absent for a version that recorded none of it. */
  generated: wikiGeneratedSchema.optional(),
  /** The shown version's trust, from its change item; absent for a legacy version. */
  trust: storedTrustSchema.optional(),
  /** The page's owner (not versioned); absent when none, or when they cannot read the page. */
  owner: wikiPersonSchema.optional(),
  /**
   * The verification of the shown version; absent when it is not verified (a newer version is
   * never verified by an older one's attestation).
   */
  verification: z
    .object({ by: wikiPersonSchema, version: z.int().min(1), at: z.string() })
    .optional(),
  /**
   * "May be stale" for this viewer: a source changed that they may see (`source_changed`), or
   * `wiki.reviewAfterDays` passed since its verification or publication (`review_due`). `since`
   * is the date the period counts from, or the source change's.
   */
  stale: z
    .object({ reason: z.enum(["source_changed", "review_due"]), since: z.string() })
    .optional(),
  /** The caller may change the owner, verify the shown version, or withdraw its verification. */
  can: z
    .object({ setOwner: z.boolean(), verify: z.boolean(), withdrawVerification: z.boolean() })
    .optional(),
});
export type WikiPageView = z.infer<typeof wikiPageViewSchema>;

/** The generated log: publications of pages the viewer can read, newest first. */
export const wikiLogEntrySchema = z.object({
  page: wikiPageRefSchema,
  version: z.int().min(1),
  publishedAt: z.string(),
  authorName: z.string().nullable(),
});
export type WikiLogEntry = z.infer<typeof wikiLogEntrySchema>;

export const wikiIndexSchema = z.object({
  pages: z.array(wikiPageSummarySchema),
  log: z.array(wikiLogEntrySchema),
});
export type WikiIndex = z.infer<typeof wikiIndexSchema>;

// ── Drafts ───────────────────────────────────────────────────────────────────────────────

export const wikiDraftStatusSchema = z.enum(["open", "published", "discarded", "stale"]);
export type WikiDraftStatus = z.infer<typeof wikiDraftStatusSchema>;

export const wikiDraftSchema = z.object({
  /** Its item's ID: open it at `/wiki/changes/:changeSetId?item=:id`. */
  id: z.uuid(),
  /** The change set holding it (absent from the previous API). */
  changeSetId: z.uuid().optional(),
  /** The page it edits; null for a new page. */
  pageId: documentIdSchema.nullable(),
  baseVersion: z.int().min(1).nullable(),
  title: z.string(),
  pageType: wikiPageTypeSchema,
  markdown: z.string(),
  trust: storedTrustSchema,
  origin: z.enum(["user", "agent", "ingestion"]),
  status: wikiDraftStatusSchema,
  /** `not_checked` when the contradiction pass could not run; null when none was attempted. */
  contradictionCheck: z.enum(["checked", "not_checked"]).nullable(),
  createdBy: userIdSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Where the draft comes from. `kind`: proposed by the agent, saved from an answer, written by
   * a person, or a summary of a file. `conversation` only when the caller may open it (`title`
   * null when they fail its label); `summaryOf` titled only while the file exists.
   */
  provenance: z.object({
    kind: z.enum(["agent", "answer", "person", "summary"]),
    createdByName: z.string().nullable(),
    conversation: z.object({ id: z.uuid(), title: z.string().nullable() }).nullable(),
    /** It was drafted from a conversation that was deleted since (no link, no title). */
    conversationDeleted: z.boolean().optional(),
    summaryOf: z
      .object({ id: documentIdSchema, title: z.string().nullable(), deleted: z.boolean() })
      .nullable(),
    /**
     * Proposed through an MCP client (mcp-write-tools D4), by `createdByName`: the client's host
     * as ESI derives it from the registered client ID (null for ESI's own client), and the
     * client's summary of the change. Absent for every other draft.
     */
    mcp: z.object({ clientHost: z.string().nullable(), summary: z.string() }).optional(),
  }),
  /** Its label sources, by title; a deleted source has no title. */
  sources: z.array(
    z.object({ id: documentIdSchema, title: z.string().nullable(), deleted: z.boolean() }),
  ),
  /** The Wiki folder a new page is published into; null or absent: the Wiki root. */
  folderId: folderIdSchema.nullable().optional(),
});
export type WikiDraft = z.infer<typeof wikiDraftSchema>;

/** "Save this answer to the wiki": a draft from an assistant message. */
export const saveToWikiRequestSchema = z.object({
  title: wikiTitleSchema.optional(),
  pageType: wikiPageTypeSchema.optional(),
  /** The project to draft in: required from a standalone conversation, refused otherwise. */
  targetProjectId: z.uuid().optional(),
  /** A Wiki folder of the target project for the page. */
  folderId: optionalFolderIdSchema,
});

/**
 * A saved answer: its change item (`id`) and the one-item change set holding it, reviewed at
 * `/wiki/changes/:changeSetId?item=:id`.
 */
export const savedToWikiSchema = z.object({ id: z.uuid(), changeSetId: z.uuid() });
export type SavedToWiki = z.infer<typeof savedToWikiSchema>;

/** A restricted source on the declassification review screen. */
export const restrictedSourceSchema = z.object({
  id: documentIdSchema,
  title: z.string(),
  /** Intended readers who cannot read this source right now. */
  cannotRead: z.int().min(0),
});
export type RestrictedSource = z.infer<typeof restrictedSourceSchema>;

/**
 * What publishing a draft would do, before confirming: who could read the page without
 * declassification, and the declassification review data when the caller may declassify.
 */
export const publishPreviewSchema = z.object({
  readers: z.array(z.object({ userId: userIdSchema, displayName: z.string().nullable() })),
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  /** Users the chosen readers name (who would read it if every source allowed). */
  intendedReaderCount: z.int().min(0),
  declassification: z.discriminatedUnion("available", [
    z.object({ available: z.literal(true), sources: z.array(restrictedSourceSchema) }),
    z.object({
      available: z.literal(false),
      /** `none_needed`: every intended reader can read every source. */
      reason: z.enum([
        "none_needed",
        "not_admin",
        "forbidden_by_setting",
        "break_glass",
        "cannot_read_sources",
      ]),
    }),
  ]),
});
export type PublishPreview = z.infer<typeof publishPreviewSchema>;

/**
 * A change item's publish options: readers of the page (default: the project audience) and an
 * optional declassification.
 */
export const publishItemOptionsSchema = z.object({
  readers: z.array(readerSchema).min(1).max(100).optional(),
  /** "Publish with wider readers": the declassification step; its reason is required. */
  declassify: z.object({ reason: z.string().max(2000) }).optional(),
  /**
   * The publisher's choice of Wiki folder for a new page (absent: the draft's; null: the root).
   * Ignored for an edit, which keeps the page's folder.
   */
  folderId: optionalFolderIdSchema,
});
export type PublishItemOptions = z.infer<typeof publishItemOptionsSchema>;

export const publishedWikiPageSchema = z.object({
  page: wikiPageSummarySchema,
  declassificationId: z.uuid().nullable(),
});
export type PublishedWikiPage = z.infer<typeof publishedWikiPageSchema>;

// ── Review queue ─────────────────────────────────────────────────────────────────────────

export const wikiReviewItemSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["source_changed", "contradiction", "redeclassify"]),
  page: wikiPageRefSchema.nullable(),
  changeItemId: z.uuid().nullable().optional(),
  source: wikiPageRefSchema.nullable(),
  detail: z.string().nullable(),
  createdAt: z.string(),
});
export type WikiReviewItem = z.infer<typeof wikiReviewItemSchema>;

// ── Declassification ─────────────────────────────────────────────────────────────────────

export const declassificationSinkSchema = z.enum(["wiki_publish", "save_file"]);
export type DeclassificationSink = z.infer<typeof declassificationSinkSchema>;

export const declassificationSchema = z.object({
  id: z.uuid(),
  projectId: projectIdSchema,
  documentId: documentIdSchema,
  version: z.int().min(1),
  /** Null when the viewer cannot read the content. */
  title: z.string().nullable(),
  sink: declassificationSinkSchema,
  reason: z.string(),
  actorId: userIdSchema,
  actorName: z.string().nullable(),
  sourceCount: z.int().min(0),
  createdAt: z.string(),
  withdrawnAt: z.string().nullable(),
});
export type Declassification = z.infer<typeof declassificationSchema>;

/** Backoffice report row: metadata and counts only; titles only for content the admin reads. */
export const declassificationReportRowSchema = declassificationSchema.extend({
  projectName: z.string(),
});
export type DeclassificationReportRow = z.infer<typeof declassificationReportRowSchema>;

// ── OKF export ───────────────────────────────────────────────────────────────────────────

export const wikiExportSchema = z.object({
  id: z.uuid(),
  status: z.enum(["pending", "ready", "failed"]),
  pageCount: z.int().min(0).nullable(),
  createdAt: z.string(),
});
export type WikiExport = z.infer<typeof wikiExportSchema>;
